import { FC, useId } from 'react';

import { ContactHistoryKind } from '@/api/address-book/types';
import type { ContactHistoryEntry } from '@/api/address-book-bff/types';
import type { MemberActivityContact } from '@/api/gamma-team/types';
import ChevronDown from '@/assets/svg/ui-elements/chevron-down.svg';
import { Badge, ExpandToggle, Link, theme, Typography } from '@/design-system';

import { UNNAMED_CONTACT } from '../../address-book/modules/address-book-board/utils/contact-presentation';
import {
  COMMUNICATION_KIND_BADGE_COLOR,
  COMMUNICATION_KIND_LABELS,
  isCommunicationKind,
} from '../../user-profile/modules/history-tab/constants';
import {
  communicationTextOf,
  isEdited,
} from '../../user-profile/modules/history-tab/describe-entry';
import { formatEntryTimestamp } from '../../user-profile/modules/history-tab/format-entry-time';
import { AttachmentList } from '../../user-profile/modules/history-tab/history-entry';
import { EntryCard } from '../../user-profile/modules/history-tab/styled';
import { toContactHistoryHref } from './contact-href';
import {
  ContactCard,
  ContactEntries,
  ContactHeader,
  ContactMeta,
  ContactTitle,
  EntryMetaRow,
  EntryText,
} from './styled';

export const DELETED_CONTACT_LABEL = 'Deleted contact';

const entryCountLabel = (count: number): string => `${count} ${count === 1 ? 'entry' : 'entries'}`;

const Dot: FC = () => (
  <Typography as="span" variant="body4" color={theme.colors.text.tertiary}>
    ·
  </Typography>
);

export const ActivityEntry: FC<{ entry: ContactHistoryEntry }> = ({ entry }) => {
  const kind = isCommunicationKind(entry.kind) ? entry.kind : ContactHistoryKind.Note;
  const text = communicationTextOf(entry);
  const edited = isEdited(entry);

  return (
    <EntryCard as="article" data-entry-kind={entry.kind}>
      <EntryMetaRow>
        <Badge
          label={COMMUNICATION_KIND_LABELS[kind]}
          color={COMMUNICATION_KIND_BADGE_COLOR[kind]}
          variant="subtle"
          size="sm"
        />
        <Typography
          as="time"
          variant="body4"
          color={theme.colors.text.tertiary}
          dateTime={entry.createdAt}
        >
          {formatEntryTimestamp(entry.createdAt)}
        </Typography>
        {edited && (
          <>
            <Dot />
            <Typography
              as="span"
              variant="body4"
              color={theme.colors.text.tertiary}
              title={`Edited ${formatEntryTimestamp(entry.updatedAt)}`}
            >
              edited
            </Typography>
          </>
        )}
      </EntryMetaRow>

      {text && <EntryText>{text}</EntryText>}
      <AttachmentList key={entry.id} attachments={entry.attachments} />
    </EntryCard>
  );
};

interface ContactActivityProps {
  item: MemberActivityContact;
  expanded: boolean;
  onToggle: () => void;
}

export const ContactActivity: FC<ContactActivityProps> = ({ item, expanded, onToggle }) => {
  const panelId = useId();
  const { contact } = item;
  const name = contact.name?.trim() || (contact.deleted ? DELETED_CONTACT_LABEL : UNNAMED_CONTACT);
  const href = toContactHistoryHref(contact);

  return (
    <ContactCard data-contact-id={contact.id}>
      <ContactHeader>
        <ExpandToggle
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${name}`}
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <ChevronDown style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </ExpandToggle>

        <ContactTitle>
          {href ? (
            <Link to={href} size="regular">
              {name}
            </Link>
          ) : (
            <Typography variant="body3" fontWeight={500} color={theme.colors.text.primary} noWrap>
              {name}
            </Typography>
          )}

          <ContactMeta>
            <Typography variant="body4" color={theme.colors.text.secondary}>
              {entryCountLabel(item.entries)}
            </Typography>
            {item.kinds.map((kind) => (
              <Badge
                key={kind}
                label={COMMUNICATION_KIND_LABELS[kind]}
                color={COMMUNICATION_KIND_BADGE_COLOR[kind]}
                variant="subtle"
                size="sm"
              />
            ))}
            <Typography
              as="time"
              variant="body4"
              color={theme.colors.text.tertiary}
              dateTime={item.lastEntryAt}
            >
              Last {formatEntryTimestamp(item.lastEntryAt)}
            </Typography>
          </ContactMeta>
        </ContactTitle>
      </ContactHeader>

      {expanded && (
        <ContactEntries id={panelId}>
          {item.items.map((entry) => (
            <ActivityEntry key={entry.id} entry={entry} />
          ))}
        </ContactEntries>
      )}
    </ContactCard>
  );
};
